import React from 'react'
import { Box } from '@mui/material'

import { ASSET_URLS } from '../../constants'

interface MicrosoftSignInButtonProps {
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

/**
 * Microsoft Sign In Button following official Microsoft branding guidelines.
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-branding-in-apps
 *
 * Specs (light theme):
 * - Height: 41px
 * - Padding: 12px horizontal, 12px gap between logo and text
 * - Border: 1px solid #8C8C8C
 * - Background: #FFFFFF
 * - Font: Segoe UI, 15px, weight 600, color #5E5E5E
 */
export const MicrosoftSignInButton: React.FC<MicrosoftSignInButtonProps> = ({
  onClick,
  disabled = false,
  loading = false,
}) => {
  const isDisabled = disabled || loading

  return (
    <Box
      component='button'
      onClick={onClick}
      disabled={isDisabled}
      aria-label='Sign in with Microsoft'
      sx={{
        width: '100%',
        height: '41px',
        padding: '0 12px',
        border: '1px solid #8C8C8C',
        borderRadius: '4px',
        backgroundColor: '#FFFFFF',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        transition: 'background-color 0.2s',
        '&:hover:not(:disabled)': {
          backgroundColor: '#F5F5F5',
        },
        '&:focus': {
          outline: '2px solid #0078D4',
          outlineOffset: '2px',
        },
      }}
    >
      <img
        src={ASSET_URLS.microsoftLogo}
        alt=''
        aria-hidden='true'
        style={{ width: '21px', height: '21px' }}
      />
      <span
        style={{
          fontFamily: 'sans-serif',
          fontSize: '15px',
          fontWeight: 600,
          color: '#5E5E5E',
        }}
      >
        {loading ? 'Checking...' : 'Sign in with Microsoft'}
      </span>
    </Box>
  )
}
